import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, Calendar, MapPin, Clock, PoundSterling,
  Baby, ExternalLink, AlertCircle, Check, Info
} from "lucide-react";

const CreateBooking = () => {
  const { poolId } = useParams();
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pool, setPool] = useState(null);
  const [costPreview, setCostPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    scheduled_date: "",
    scheduled_end: "",
    estimated_cost: "",
    activity_type: ""
  });

  const activityTypes = [
    "Park excursion",
    "Soft play",
    "Museum visit",
    "Swimming",
    "Arts & crafts",
    "Sports activity",
    "Nature walk",
    "Library visit",
    "Zoo/Farm visit",
    "Other"
  ];

  useEffect(() => {
    fetchPool();
  }, [poolId]);

  const fetchPool = async () => {
    try {
      const res = await axios.get(`${API}/sitter-pools`);
      const foundPool = res.data.pools?.find(p => p.pool_id === poolId);
      if (foundPool) {
        setPool(foundPool);
        // Calculate preview
        calculateCostPreview(foundPool, 0);
      } else {
        toast.error("Pool not found");
        navigate("/sitter-pools");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const calculateCostPreview = (poolData, cost) => {
    if (!poolData) return;
    
    const commission = cost * 0.10;
    const totalCost = cost + commission;
    
    let totalChildren = 0;
    const familyShares = [];
    
    for (const member of poolData.members || []) {
      totalChildren += member.children_count || 0;
    }
    
    for (const member of poolData.members || []) {
      const childrenCount = member.children_count || 0;
      const shareRatio = totalChildren > 0 ? childrenCount / totalChildren : 1 / (poolData.members?.length || 1);
      const shareAmount = totalCost * shareRatio;
      
      familyShares.push({
        name: member.parent_name,
        children: childrenCount,
        amount: shareAmount,
        isMe: member.user_id === user?.user_id
      });
    }
    
    setCostPreview({
      baseCost: cost,
      commission,
      totalCost,
      totalChildren,
      shares: familyShares
    });
  };

  const handleCostChange = (value) => {
    const cost = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, estimated_cost: value }));
    calculateCostPreview(pool, cost);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.location || !formData.scheduled_date || !formData.scheduled_end || !formData.estimated_cost) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/sitter-pools/${poolId}/bookings`, {
        pool_id: poolId,
        title: formData.title,
        description: formData.description,
        location: formData.location,
        scheduled_date: new Date(formData.scheduled_date).toISOString(),
        scheduled_end: new Date(formData.scheduled_end).toISOString(),
        estimated_cost: parseFloat(formData.estimated_cost),
        sitter_service: "sitters.co.uk",
        activity_type: formData.activity_type
      });
      
      toast.success("Booking created! Now book your sitter.");
      navigate(`/sitter-pools/${poolId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Header */}
      <div className="bg-gradient-to-r from-coral-500 to-coral-600 text-white p-6 pt-8">
        <div className="flex items-center gap-3">
          <Link to={`/sitter-pools/${poolId}`} className="p-2 -ml-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Create Booking
            </h1>
            <p className="text-coral-100 text-sm">{pool?.name}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-6">
        {/* Activity Details */}
        <div className="bg-white rounded-xl p-4 border border-slate-100 space-y-4">
          <h2 className="font-semibold text-slate-800">Activity Details</h2>
          
          <div>
            <Label htmlFor="title">Booking Title *</Label>
            <Input
              id="title"
              placeholder="e.g., Saturday Park Trip"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="mt-1"
              required
              data-testid="booking-title-input"
            />
          </div>

          <div>
            <Label htmlFor="activity_type">Activity Type</Label>
            <select
              id="activity_type"
              value={formData.activity_type}
              onChange={(e) => setFormData(prev => ({ ...prev, activity_type: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="activity-type-select"
            >
              <option value="">Select activity type</option>
              {activityTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              placeholder="What will the kids be doing?"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              rows={3}
              data-testid="booking-description-input"
            />
          </div>

          <div>
            <Label htmlFor="location">Location *</Label>
            <div className="relative mt-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="location"
                placeholder="e.g., Hyde Park, London"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="pl-10"
                required
                data-testid="booking-location-input"
              />
            </div>
          </div>
        </div>

        {/* Date & Time */}
        <div className="bg-white rounded-xl p-4 border border-slate-100 space-y-4">
          <h2 className="font-semibold text-slate-800">Date & Time</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="scheduled_date">Start *</Label>
              <Input
                id="scheduled_date"
                type="datetime-local"
                value={formData.scheduled_date}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                className="mt-1"
                required
                data-testid="booking-start-input"
              />
            </div>
            <div>
              <Label htmlFor="scheduled_end">End *</Label>
              <Input
                id="scheduled_end"
                type="datetime-local"
                value={formData.scheduled_end}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_end: e.target.value }))}
                className="mt-1"
                required
                data-testid="booking-end-input"
              />
            </div>
          </div>
        </div>

        {/* Cost & Split */}
        <div className="bg-white rounded-xl p-4 border border-slate-100 space-y-4">
          <h2 className="font-semibold text-slate-800">Cost & Split</h2>
          
          <div>
            <Label htmlFor="estimated_cost">Estimated Sitter Cost (£) *</Label>
            <div className="relative mt-1">
              <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="estimated_cost"
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g., 80.00"
                value={formData.estimated_cost}
                onChange={(e) => handleCostChange(e.target.value)}
                className="pl-10"
                required
                data-testid="booking-cost-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Check <a href="https://sitters.co.uk" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline">Sitters.co.uk</a> for current rates
            </p>
          </div>

          {/* Cost Preview */}
          {costPreview && parseFloat(formData.estimated_cost) > 0 && (
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Info className="w-4 h-4 text-teal-500" />
                Cost Split Preview
              </div>
              
              <div className="space-y-2">
                {costPreview.shares.map((share, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-2 rounded-lg ${share.isMe ? 'bg-teal-50 border border-teal-200' : 'bg-white'}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-coral-400 flex items-center justify-center text-white text-xs font-bold">
                        {share.name?.[0] || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">
                          {share.name}
                          {share.isMe && <span className="text-teal-600 ml-1">(You)</span>}
                        </p>
                        <p className="text-xs text-slate-500">{share.children} children</p>
                      </div>
                    </div>
                    <span className="font-semibold text-slate-800">
                      £{share.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="pt-3 border-t border-slate-200 space-y-1 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Sitter cost:</span>
                  <span>£{costPreview.baseCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>KiddiPlay fee (10%):</span>
                  <span>£{costPreview.commission.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-slate-800">
                  <span>Total:</span>
                  <span>£{costPreview.totalCost.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-teal-600 mt-0.5" />
            <div className="text-sm text-teal-800">
              <p className="font-medium mb-1">What happens next?</p>
              <ol className="space-y-1 text-teal-700">
                <li>1. All pool members will see this booking</li>
                <li>2. Everyone confirms their participation</li>
                <li>3. One member books the sitter on Sitters.co.uk</li>
                <li>4. Costs are automatically split!</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-coral-500 hover:bg-coral-600 text-white py-6 rounded-xl text-lg font-medium"
          data-testid="submit-booking-btn"
        >
          {loading ? "Creating..." : "Create Booking"}
        </Button>

        <p className="text-xs text-center text-slate-500">
          By creating a booking, you agree to the Sitter Pool terms and conditions
        </p>
      </form>
    </div>
  );
};

export default CreateBooking;
